import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import {
  ApiQueryOperator,
  EmployeeStatus,
  IAction,
  IApiList,
  ICalculatorSettingsOverride,
  ICreateEmployeeSite,
  IEmployee,
  IEmployeeIntegration,
  IExportCalculatorSettings,
  IPOSProvider,
  IPOSResults,
  ITipCalculatorErrorGroup,
  ITipCalculatorRequest,
  ITipCalculatorResult,
} from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import { TipCalculator } from './types';

type ITipCalculatorRequestFulfilled = { ok: true; value: ITipCalculatorResult };
type ITipCalculatorRequestRejected = { ok: false; tipCalcRequest: ITipCalculatorRequest; apiError: any };
type ITipCalculatorRequestSettled = ITipCalculatorRequestFulfilled | ITipCalculatorRequestRejected;

function* processTipCalculatorRequestSettled(tipCalcRequest: ITipCalculatorRequest): Generator<any, ITipCalculatorRequestSettled, any> {
  try {
    const response: ApiResponse<ITipCalculatorResult> = yield call(API.TipCalculator.shareCalculation, tipCalcRequest);
    const tipCalcResponse: ITipCalculatorResult = tipCalcRequest.segmentId ? { ...response.body, segmentId: tipCalcRequest.segmentId } : response.body;
    return { ok: true, value: tipCalcResponse };
  } catch (error: any) {
    return { ok: false, tipCalcRequest, apiError: error };
  }
}

interface IGetCalculatedTipsPayload {
  tipCalcRequests: ITipCalculatorRequest[];
  posResults: IPOSResults;
  creditCardFeePercent: number;
  onSuccess: (data: ITipCalculatorResult[]) => void;
  onError: (error: any) => void;
}

export function* getCalculatedTips(action: IAction<TipCalculator.GET_CALCULATED_TIPS_REQUEST, IGetCalculatedTipsPayload>) {
  try {
    const { tipCalcRequests } = action.payload;

    // Run all requests, but settle them individually so one failure doesn't prevent other
    // requests from completing. This lets us aggregate errors across all failing requests.
    const settledResults: ITipCalculatorRequestSettled[] = yield all(tipCalcRequests.map((tipCalcRequest: ITipCalculatorRequest) => call(processTipCalculatorRequestSettled, tipCalcRequest)));

    const { fulfilled, rejected } = settledResults.reduce<{ fulfilled: ITipCalculatorResult[]; rejected: ITipCalculatorErrorGroup[] }>(
      (acc, result) => {
        if (result.ok) {
          acc.fulfilled.push(result.value);
        } else {
          const rejected = result as ITipCalculatorRequestRejected;
          acc.rejected.push({
            errors: Array.isArray(rejected.apiError?.body?.errors) ? rejected.apiError.body.errors : [],
            tipCalcRequests: [rejected.tipCalcRequest],
          });
        }
        return acc;
      },
      { fulfilled: [], rejected: [] }
    );

    if (rejected.length) {
      const flattenedErrors = rejected.flatMap((group) => group.errors);
      yield put(Actions.getCalculatedTipsError(flattenedErrors));
      yield call(action.payload.onError, { errorsByRequest: rejected });
      return;
    }

    // Execute on success function
    yield call(action.payload.onSuccess, fulfilled);
    yield put(Actions.getCalculatedTipsSuccess(fulfilled));
  } catch (error: any) {
    // This should be rare since we settle individual request errors above.
    yield put(Actions.getCalculatedTipsError(error?.body?.errors));
    yield call(action.payload.onError, error);
  }
}

interface IGetPOSDataPayload {
  siteId: string;
  date: string;
  onSuccess: (data: IPOSResults) => void;
  onError: (error: any) => void;
  skipNoLabor?: boolean;
  /** The settings a previous calculation was made under, when the caller asked to redo a paid date with them. */
  settingsOverride?: ICalculatorSettingsOverride;
}

/**
 * Retrieves POS data for a date. A POST rather than a GET because the settings override travels in the body: this
 * is a read whose parameters are too large for a query string, not a write.
 *
 * Without an override the server assembles the data from the site's current config; with one it assembles from
 * those settings instead.
 */
export function* getPOSData(action: IAction<TipCalculator.GET_POS_REQUEST, IGetPOSDataPayload>) {
  const { skipNoLabor, siteId, date, onSuccess, onError, settingsOverride } = action.payload;
  try {
    const response: ApiResponse<IPOSResults> = yield call(API.Integrations.postLaborTicketDataForDate, siteId, date, skipNoLabor, settingsOverride);

    // Execute on success function
    if (onSuccess) yield call(onSuccess, response.body);

    yield put(Actions.getPOSDataSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getPOSDataError(error.body?.errors));
    if (onError) yield call(onError, error.body?.errors);
  }
}

interface ILinkEmployeePayload {
  employee: IEmployee;
  integration: IEmployeeIntegration;
  site: ICreateEmployeeSite;
  onSuccess: (employee: IEmployee) => void;
  onError: (error: any) => void;
}

export function* linkEmployee(action: IAction<TipCalculator.LINK_EMPLOYEE_REQUEST, ILinkEmployeePayload>) {
  try {
    const { employee, integration, site } = action.payload;

    let updatedEmployee: ApiResponse<IEmployee>;

    // update employee status to approved if employee is dismissed
    if (employee.status === EmployeeStatus.DISMISSED) {
      updatedEmployee = yield call(API.Employees.update, employee.id, { status: EmployeeStatus.APPROVED });
    }

    // get site
    const selectedSite = employee.sites.find((s) => s.id === site.id);

    // create new site role if does not exist
    if (!selectedSite) updatedEmployee = yield call(API.Employees.addSite, employee.id, site);

    // update existing site role status to approved if dismissed
    if (selectedSite && selectedSite.status === EmployeeStatus.DISMISSED) {
      updatedEmployee = yield call(API.Employees.updateSite, employee.id, site.id, { ...selectedSite, status: EmployeeStatus.APPROVED });
    }

    // link employee to integration
    yield call(API.Employees.addIntegration, employee.id, integration);

    // Add the integration to the employee
    const response: IEmployee = { ...employee, ...updatedEmployee?.body, integrations: [...employee.integrations, integration] };

    // Execute on success function
    yield call(action.payload.onSuccess, response);
    yield put(Actions.linkEmployeeSuccess(response));
  } catch (error: any) {
    yield put(Actions.linkEmployeeError(error.body?.errors));
    yield call(action.payload.onError, error.body?.errors);
  }
}

interface IUnLinkEmployeePayload {
  employee: IEmployee;
  integration: IEmployeeIntegration;
  onSuccess: (employee: IEmployee) => void;
}

export function* unlinkEmployee(action: IAction<TipCalculator.UNLINK_EMPLOYEE_REQUEST, IUnLinkEmployeePayload>) {
  try {
    const { employee, integration } = action.payload;
    yield call(API.Employees.deleteIntegration, employee.id, integration);

    // Reload the employee
    const integrations = employee.integrations.filter((i) => i.type !== integration.type && i.siteId !== integration.siteId);
    const response: IEmployee = { ...employee, integrations };

    yield call(action.payload.onSuccess, response);
    yield put(Actions.unlinkEmployeeSuccess(response));
    yield call(toast, SuccessToastContainer({ message: 'User disconnected from integration successfully' }));
  } catch (error: any) {
    yield put(Actions.unlinkEmployeeError(error.body?.errors));
  }
}

interface IGetMatchEmployeesPayload {
  siteId: string;
  provider: IPOSProvider;
  onSuccess: (employees: IEmployee[]) => void;
  onDuplicateProviderMatch: (employee: IEmployee) => void;
  onError: (error: any) => void;
}

export function* getMatchEmployees(action: IAction<TipCalculator.GET_MATCH_EMPLOYEES_REQUEST, IGetMatchEmployeesPayload>) {
  try {
    const { siteId, provider } = action.payload;
    const normalizedLastName = provider.lastName?.trim().toLowerCase();
    const normalizedFirstName = provider.firstName?.trim().toLowerCase();

    const duplicateProviderMatchOptions: IApiQueryOptions = {
      q: [
        { key: 'integrations.id', value: provider.employeeId, operator: ApiQueryOperator.eq },
        { key: 'integrations.type', value: provider.type, operator: ApiQueryOperator.eq },
        { key: 'type', value: 'admin', operator: ApiQueryOperator.neq },
      ],
      includeDismissed: true,
      limit: 1,
    };

    const employeeIdMatchOptions: IApiQueryOptions = {
      q: [
        { key: 'sites.employee_id', value: `^${provider.customerEmployeeId}`, operator: ApiQueryOperator.like },
        { key: 'type', value: 'admin', operator: ApiQueryOperator.neq },
      ],
      limit: 0,
    };

    const lastNameMatchOptions: IApiQueryOptions = {
      q: [
        { key: 'last_name', value: provider.lastName, operator: ApiQueryOperator.like },
        { key: 'type', value: 'admin', operator: ApiQueryOperator.neq },
      ],
      limit: 0,
    };
    // find by employee id or last name (fuzzy)
    const [duplicateProviderMatchResponse, employeeIdMatchResponse, lastNameMatchResponse]: ApiResponse<IApiList<IEmployee>>[] = yield all([
      call(API.Sites.listEmployees, siteId, duplicateProviderMatchOptions),
      provider.customerEmployeeId ? call(API.Sites.listEmployees, siteId, employeeIdMatchOptions) : undefined,
      provider.lastName ? call(API.Sites.listEmployees, siteId, lastNameMatchOptions) : undefined,
    ]);

    if (duplicateProviderMatchResponse && duplicateProviderMatchResponse.body.data.length === 1) {
      yield call(action.payload.onDuplicateProviderMatch, duplicateProviderMatchResponse.body.data[0]);
      yield put(Actions.getMatchEmployeesSuccess(duplicateProviderMatchResponse.body.data));
      return;
    }

    // find exact match using last name (fuzzy) & employee id (exact case insensitive)
    const exactMatchResponse = normalizedLastName && employeeIdMatchResponse?.body.data.filter((emp: IEmployee) => emp.lastName.trim().toLowerCase().includes(normalizedLastName));
    if (exactMatchResponse?.length) {
      yield call(action.payload.onSuccess, exactMatchResponse);
      yield put(Actions.getMatchEmployeesSuccess(exactMatchResponse));
      return;
    }

    // find matches by first and last name (fuzzy)
    const fullNameMatchResponse = normalizedFirstName && lastNameMatchResponse?.body.data.filter((emp: IEmployee) => emp.firstName.trim().toLowerCase().includes(normalizedFirstName));
    if (fullNameMatchResponse?.length) {
      yield call(action.payload.onSuccess, fullNameMatchResponse);
      yield put(Actions.getMatchEmployeesSuccess(fullNameMatchResponse));
      return;
    }

    const mergeMatches = [];
    if (employeeIdMatchResponse) mergeMatches.push(...employeeIdMatchResponse.body.data);
    if (lastNameMatchResponse) mergeMatches.push(...lastNameMatchResponse.body.data);
    const uniqueEmployees = [...new Map(mergeMatches.map((item) => [item.id, item])).values()]; // create a unique set of employees from id

    yield call(action.payload.onSuccess, uniqueEmployees);
    yield put(Actions.getMatchEmployeesSuccess(uniqueEmployees));
  } catch (error: any) {
    yield call(action.payload.onError, error.body?.errors);
    yield put(Actions.getMatchEmployeesError(error.body?.errors));
  }
}

export function* exportCalculatorSettings(action: IAction<TipCalculator.EXPORT_CALCULATOR_SETTINGS_REQUEST, { siteId: string; onSuccess: (settings: IExportCalculatorSettings) => void }>) {
  try {
    const response: ApiResponse<IExportCalculatorSettings> = yield call(API.DataExport.siteCalculatorSettings, action.payload.siteId);

    // Execute on success function
    yield call(action.payload.onSuccess, response.body);

    yield put(Actions.exportCalculatorSettingsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.exportCalculatorSettingsError(error.body?.errors));
  }
}

interface IImportPayload {
  siteId: string;
  body: IExportCalculatorSettings;
  onSuccess: () => void;
  onError: () => void;
}

export function* importCalculatorSettings(action: IAction<TipCalculator.IMPORT_CALCULATOR_SETTINGS_REQUEST, IImportPayload>) {
  try {
    const { siteId, body } = action.payload;
    yield call(API.DataImport.siteCalculatorSettings, siteId, body);

    // update site id in the body so it can be saved in the state
    const newCalculatorSettings = {
      ...body,
      ...(body.ruleSettings && { ruleSettings: { ...body.ruleSettings, siteId } }),
      ...(body.segmentSettings && {
        segmentSettings: {
          ...body.segmentSettings,
          ...(body.segmentSettings.segments && { segments: body.segmentSettings.segments.map((segment) => ({ ...segment, siteId })) }),
        },
      }),
    };
    yield put(Actions.importCalculatorSettingsSuccess(newCalculatorSettings));

    // Execute on success function
    yield call(action.payload.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode }, true);
    yield call(action.payload.onError);
    yield put(Actions.importCalculatorSettingsError(formattedError));
  }
}

export function* watchGetPOSData() {
  yield takeLatest(TipCalculator.GET_POS_REQUEST, getPOSData);
}

export function* watchGetCalculatedTips() {
  yield takeEvery(TipCalculator.GET_CALCULATED_TIPS_REQUEST, getCalculatedTips);
}

export function* watchLinkEmployee() {
  yield takeEvery(TipCalculator.LINK_EMPLOYEE_REQUEST, linkEmployee);
}

export function* watchUnLinkEmployee() {
  yield takeEvery(TipCalculator.UNLINK_EMPLOYEE_REQUEST, unlinkEmployee);
}

export function* watchMatchEmployees() {
  yield takeEvery(TipCalculator.GET_MATCH_EMPLOYEES_REQUEST, getMatchEmployees);
}

export function* watchExportCalculatorSettings() {
  yield takeLatest(TipCalculator.EXPORT_CALCULATOR_SETTINGS_REQUEST, exportCalculatorSettings);
}

export function* watchImportCalculatorSettings() {
  yield takeLatest(TipCalculator.IMPORT_CALCULATOR_SETTINGS_REQUEST, importCalculatorSettings);
}

function* tipCalculatorSaga() {
  yield all([fork(watchGetPOSData), fork(watchGetCalculatedTips), fork(watchLinkEmployee), fork(watchUnLinkEmployee), fork(watchMatchEmployees), fork(watchExportCalculatorSettings), fork(watchImportCalculatorSettings)]);
}

export { tipCalculatorSaga };
